/**
 * Persist the shape currently confirmed in the drawing store as a mission
 * area. Complements "Send to TAK": areas are stored in VTAK (unit-scoped,
 * broadcast by the areas reference source) rather than round-tripped through
 * TAK Server. On success the area is appended to the local area shapes so it
 * renders immediately, ahead of the next broadcast poll.
 */

import type { AreaGeometry, AreaKind, DrawingTool, GeoPoint, TakShape } from '@vtak/shared';
import { argbIntToHex } from '@vtak/shared';
import type { ShapeConfig } from '@/stores/drawing-store';
import { workspaceClient } from '@/services/workspace-client';
import { useShapesStore } from '@/stores/shapes-store';

/** Result of a save attempt. */
export interface SaveAreaResult {
  ok: boolean;
  error: string;
}

/** Drawing tools that can be persisted as an area. */
const AREA_TOOLS: ReadonlySet<DrawingTool> = new Set(['polygon', 'rectangle', 'circle', 'freeform']);

/** True when the active tool + geometry can be saved as an area. */
export function canSaveAsArea(tool: DrawingTool | null, vertices: GeoPoint[], radiusMeters: number): boolean {
  if (!tool || !AREA_TOOLS.has(tool)) return false;
  if (tool === 'circle') return vertices.length >= 1 && radiusMeters > 0;
  return vertices.length >= 3;
}

/** Map the drawing state to persistable area geometry. */
function toGeometry(tool: DrawingTool, vertices: GeoPoint[], radiusMeters: number): AreaGeometry | null {
  if (tool === 'circle') {
    const center = vertices[0];
    if (!center || radiusMeters <= 0) return null;
    return { shapeType: 'circle', center: { lat: center.lat, lon: center.lon }, radiusMeters };
  }
  if (vertices.length < 3) return null;
  return { shapeType: 'polygon', vertices: vertices.map((v) => ({ lat: v.lat, lon: v.lon })) };
}

/** Optimistically render the saved area before the next broadcast poll. */
function appendLocalAreaShape(areaId: string, name: string, geometry: AreaGeometry, config: ShapeConfig): void {
  const base = {
    uid: `area:${areaId}`,
    callsign: name,
    strokeColor: config.strokeColor,
    strokeWeight: config.strokeWeight,
    fillColor: config.fillColor,
    createdAt: new Date().toISOString(),
  };
  const shape: TakShape | null =
    geometry.shapeType === 'circle' && geometry.center && geometry.radiusMeters
      ? { ...base, shapeType: 'circle', center: geometry.center, radiusMeters: geometry.radiusMeters }
      : geometry.vertices
        ? { ...base, shapeType: 'polygon', vertices: geometry.vertices }
        : null;
  if (!shape) return;
  const store = useShapesStore.getState();
  store.setAreaShapes([...store.areaShapes, shape]);
}

/** Persist the confirmed drawing as a mission area for the unit. */
export async function saveMissionArea(
  unitId: string,
  tool: DrawingTool,
  vertices: GeoPoint[],
  radiusMeters: number,
  config: ShapeConfig,
  kind: AreaKind,
): Promise<SaveAreaResult> {
  const geometry = toGeometry(tool, vertices, radiusMeters);
  if (!geometry) return { ok: false, error: 'Shape is not a valid area' };

  const name = config.callsign.trim() || 'Mission Area';
  try {
    const area = await workspaceClient.createArea(unitId, {
      name,
      kind,
      geometry,
      color: argbIntToHex(config.strokeColor),
    });
    appendLocalAreaShape(area.id, name, geometry, config);
    return { ok: true, error: '' };
  } catch (err) {
    return { ok: false, error: err instanceof Error ? err.message : 'Save failed' };
  }
}
